import type {
    KickLogoColor,
    KickLogoShape,
    KickLogoSize,
    KickLogoVariant,
} from "@design-system/KickLogo/KickLogo";

export const kickLogoVariants: readonly KickLogoVariant[] = ["full", "icon"];

/** The three Figma artworks: KickLogo, KickIcon Square and KickIcon Rounded. */
export type KickLogoFormat = "full" | "icon-square" | "icon-circle";

type KickLogoFormatProps =
    | { variant: "full" }
    | { variant: "icon"; shape: KickLogoShape };

export const kickLogoFormats: readonly KickLogoFormat[] = [
    "full",
    "icon-square",
    "icon-circle",
];

export const kickLogoFormatProps: Record<KickLogoFormat, KickLogoFormatProps> =
    {
        full: { variant: "full" },
        "icon-square": { variant: "icon", shape: "square" },
        "icon-circle": { variant: "icon", shape: "circle" },
    };

export const kickLogoFormatLabels: Record<KickLogoFormat, string> = {
    full: "full",
    "icon-square": "icon · square",
    "icon-circle": "icon · circle",
};

export const toKickLogoFormat = ({
    variant,
    shape,
}: {
    variant: KickLogoVariant;
    shape: KickLogoShape;
}): KickLogoFormat => {
    if (variant === "full") {
        return "full";
    }
    return shape === "circle" ? "icon-circle" : "icon-square";
};

export const kickLogoColors: readonly KickLogoColor[] = ["black", "white"];

/** Every colour x transparency paint, opaque before transparent. */
export const kickLogoPaints: ReadonlyArray<{
    color: KickLogoColor;
    transparent: boolean;
}> = kickLogoColors.flatMap((color) =>
    [false, true].map((transparent) => ({ color, transparent })),
);

export const kickLogoShapes: readonly KickLogoShape[] = ["square", "circle"];

/** Largest first, in Figma's order. */
export const kickLogoSizes: readonly KickLogoSize[] = [
    "2xl",
    "xl",
    "lg",
    "md",
    "sm",
    "xs",
];

export const kickLogoSizePixels: Record<KickLogoSize, number> = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 40,
    "2xl": 48,
};
